import { Check, ChevronRight, ExternalLink, ImageIcon, Palette, Store } from "lucide-react";
import { Link, useOutletContext } from "react-router";
import { useMenus } from "../../api/queries";
import { StatusChip } from "../../components/kit";
import { Alert, ICON } from "../../components/ui";
import type { ShellContext } from "../AppShell";
import { displayUrl, QrCard } from "./QrCard";

const CUSTOMIZE = [
  {
    to: "/app/products",
    icon: ImageIcon,
    label: "Fotografii, descrieri și produse",
    sub: "Adaugi fotografii și detalii; publici separat.",
  },
  {
    to: "/app/settings",
    icon: Store,
    label: "Datele restaurantului",
    sub: "Descriere, adresă și linkuri către recenzii.",
  },
  {
    to: "/app/appearance",
    icon: Palette,
    label: "Aspect și personalizare",
    sub: "Teme, culori și logo. Acum se folosește tema Editorial.",
  },
] as const;

/**
 * Shown right after the restaurant's first publication (spec §1A "Publică"): the live menu, the QR code with
 * PNG/SVG, the share link and where to customise later. Every step is optional; nothing here blocks the menu.
 */
export function PublishedPage() {
  const { membership } = useOutletContext<ShellContext>();
  const r = membership.restaurant;
  const menus = useMenus(r.id);
  const live = (menus.data ?? []).some((m) => m.publicVisible && !m.archived);

  return (
    <>
      <div className="stack" style={{ gap: 6 }}>
        {live && (
          <span style={{ alignSelf: "flex-start" }}>
            <StatusChip tone="success" icon={Check} label="Publicat" large />
          </span>
        )}
        <h1 className="h1">{live ? "Meniul tău este online" : "Codul și linkul meniului"}</h1>
        <p className="page-sub" style={{ margin: 0, maxWidth: 680 }}>
          Clienții îl deschid scanând codul QR sau din link. Când actualizezi meniul, linkul și codul rămân
          aceleași.
        </p>
      </div>
      {menus.isSuccess && !live && (
        <Alert tone="warning">
          Niciun meniu nu este publicat încă. Codul funcționează, dar clienții vor vedea „Meniul nu este
          publicat încă” până publici. <Link to="/app/products">Mergi la produse</Link>
        </Alert>
      )}
      <div className="published-grid">
        <QrCard publicUrl={r.publicUrl} slug={r.slug} restaurantName={r.name} />
        <div className="stack" style={{ gap: 16 }}>
          <section
            className="card stack"
            aria-labelledby="pp-menu"
            style={{ gap: 12, alignItems: "flex-start" }}
          >
            <h2 id="pp-menu" className="h2">
              Meniul public
            </h2>
            <span style={{ fontSize: 15, fontWeight: 600, wordBreak: "break-all" }}>
              {displayUrl(r.publicUrl)}
            </span>
            <span className="note">Așa îl văd clienții acum, pe telefon.</span>
            <a className="btn btn-primary" href={r.publicUrl} target="_blank" rel="noopener">
              <ExternalLink {...ICON} aria-hidden="true" />
              Deschide meniul
            </a>
          </section>
          <section className="card stack" aria-labelledby="pp-custom" style={{ gap: 8 }}>
            <h2 id="pp-custom" className="h2">
              Personalizează
            </h2>
            <span className="note">Opțional, oricând. Meniul rămâne online cât timp lucrezi.</span>
            <ul className="link-list">
              {CUSTOMIZE.map((c) => (
                <li key={c.to}>
                  <Link to={c.to}>
                    <c.icon {...ICON} aria-hidden="true" />
                    <span className="stack" style={{ gap: 2, flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: 600 }}>{c.label}</span>
                      <span className="caption muted">{c.sub}</span>
                    </span>
                    <ChevronRight {...ICON} aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
          <Link to="/app" style={{ alignSelf: "flex-start" }}>
            Mergi la prezentarea generală
          </Link>
        </div>
      </div>
    </>
  );
}
